import React, { useState } from "react";
import { Link } from "react-router-dom";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, Mail, MailCheck, ArrowLeft } from "lucide-react";

/**
 * Iter 720 — "Forgot password?" request page.
 * POST /api/auth/forgot-password always answers 204 (no account
 * enumeration), so the success state renders unconditionally and the copy
 * never says whether the email exists.
 */
export default function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const BAD_EMAIL_MSG = "Please enter a valid email address (e.g. you@example.com)";

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    // Iter 720 Addendum A — native type="email" accepts "Devi@2024"
    // (no TLD); validate properly before calling the API.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setEmailError(BAD_EMAIL_MSG);
      return;
    }
    setEmailError("");
    setLoading(true);
    try {
      await sharedApi.post("/auth/forgot-password", { email: value }, { timeout: 15000 });
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        // Backend email validation — same inline message, no toast.
        setEmailError(BAD_EMAIL_MSG);
      } else if (status === 429) {
        toast.error(t("Too many requests — please wait a minute and try again."));
      } else {
        toast.error(t("Couldn't reach the server — please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-12">
      <Seo title="Reset your password" noindex />
      <div className="rounded-3xl bg-white shadow-card p-8">
        {sent ? (
          <div data-testid="forgot-password-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 mb-3">
              <MailCheck className="w-6 h-6" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
              {t("Check your email")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{email.trim()}</span>
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {t("If an account exists for this email, a reset link is on its way. Check spam too.")}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {t("The link is valid for 1 hour.")}
            </p>
            <Link
              to="/login"
              data-testid="forgot-password-back-to-login"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 transition-colors"
            >
              {t("Back to log in")}
            </Link>
          </div>
        ) : (
          <>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[color:var(--brand-blue)] mb-4 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {t("Back to log in")}
            </Link>
            <h1 className="font-display text-3xl font-bold text-slate-900">
              {t("Reset your password")}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {t("Enter your account email and we'll send you a link (valid 1 hour).")}
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="forgot-password-form">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">
                  {t("Email")}
                </span>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="forgot-password-email-input"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    className={`w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border focus:outline-none text-sm ${
                      emailError
                        ? "border-red-300 focus:border-red-400"
                        : "border-soft focus:border-[color:var(--brand-blue)]"
                    }`}
                    placeholder="you@example.co.nz"
                  />
                </div>
                {emailError && (
                  <span
                    className="mt-1 block text-xs text-red-500"
                    data-testid="forgot-password-email-error"
                  >
                    {t(emailError)}
                  </span>
                )}
              </label>
              <button
                data-testid="forgot-password-submit"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t("Send reset link")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
